import { useState } from 'react'
import { Users, Crown, ArrowUpCircle, Bell, CheckCircle, RefreshCw, TrendingUp, Loader2, ExternalLink } from 'lucide-react'
import type { AnalysisResult, SalesOpportunity } from '@/types/halopsa'

interface AlertsTabProps {
  analysis: AnalysisResult | null
  contactIsVip?: boolean
  contactName?: string
  contactJobTitle?: string
  isDark: boolean
  salesOpportunityData?: SalesOpportunity | null
  onCreateSalesTicket?: () => Promise<{ ticketId: number } | void>
}

interface AlertCard {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  detail?: string
  borderColor: string
  bgColor: string
  darkBgColor: string
  titleColor: string
  darkTitleColor: string
  textColor: string
  darkTextColor: string
}

export function AlertsTab({ analysis, contactIsVip, contactName, contactJobTitle, isDark, salesOpportunityData, onCreateSalesTicket }: AlertsTabProps) {
  const [salesTicketState, setSalesTicketState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [salesTicketId, setSalesTicketId] = useState<number | null>(null)
  const [salesTicketError, setSalesTicketError] = useState<string | null>(null)

  if (!analysis) {
    return (
      <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
        <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Run AI Analysis first to check for alerts.</p>
      </div>
    )
  }

  const vipData = analysis.sentiment?.metrics?.vip
  const escalationData = analysis.sentiment?.escalation
  const multiUserData = analysis.sentiment?.metrics?.multiUserEvent
  const chronicData = analysis.sentiment?.metrics?.chronic

  const isVip = vipData?.isVip || (vipData?.score && vipData.score >= 7) || contactIsVip
  const needsEscalation = escalationData?.recommended || (escalationData?.score && escalationData.score >= 7)
  const isMultiUserEvent = (multiUserData as any)?.isMultiUser || (multiUserData?.score && multiUserData.score >= 5)
  const isRecurring = chronicData?.score && chronicData.score >= 7
  const hasSalesOpp = salesOpportunityData?.detected && (salesOpportunityData?.confidence ?? 0) >= 60

  const alerts: AlertCard[] = []

  if (isMultiUserEvent) {
    const scope = (multiUserData as any)?.affectedScope || (multiUserData as any)?.explanation || 'Multiple users affected'
    alerts.push({
      id: 'multiuser',
      icon: <Users className="h-5 w-5 text-white" />,
      title: 'Multi-User Event Detected',
      description: 'This issue may be affecting multiple users or is company-wide.',
      detail: scope,
      borderColor: 'border-violet-500',
      bgColor: 'bg-violet-50',
      darkBgColor: 'bg-violet-900/30',
      titleColor: 'text-violet-800',
      darkTitleColor: 'text-violet-300',
      textColor: 'text-violet-700',
      darkTextColor: 'text-violet-400',
    })
  }

  if (isVip) {
    const reason = (vipData as any)?.reason || contactJobTitle || 'Priority handling required'
    alerts.push({
      id: 'vip',
      icon: <Crown className="h-5 w-5 text-white" />,
      title: 'VIP / Executive Contact',
      description: contactName ? `${contactName}${contactJobTitle ? ` — ${contactJobTitle}` : ''}` : reason,
      detail: contactName ? reason : undefined,
      borderColor: 'border-amber-500',
      bgColor: 'bg-amber-50',
      darkBgColor: 'bg-amber-900/30',
      titleColor: 'text-amber-800',
      darkTitleColor: 'text-amber-300',
      textColor: 'text-amber-700',
      darkTextColor: 'text-amber-400',
    })
  }

  if (needsEscalation) {
    const factors = escalationData?.factors?.slice(0, 3) || []
    alerts.push({
      id: 'escalation',
      icon: <ArrowUpCircle className="h-5 w-5 text-white" />,
      title: 'Escalation Recommended',
      description: factors.length > 0 ? factors[0] : 'This ticket may require immediate escalation.',
      detail: factors.length > 1 ? factors.slice(1).join(' • ') : undefined,
      borderColor: 'border-red-500',
      bgColor: 'bg-red-50',
      darkBgColor: 'bg-red-900/30',
      titleColor: 'text-red-800',
      darkTitleColor: 'text-red-300',
      textColor: 'text-red-700',
      darkTextColor: 'text-red-400',
    })
  }

  if (isRecurring) {
    alerts.push({
      id: 'recurring',
      icon: <RefreshCw className="h-5 w-5 text-white" />,
      title: 'Recurring Issue Detected',
      description: chronicData?.explanation || 'This appears to be a recurring problem.',
      detail: 'Consider investigating root cause to prevent future occurrences.',
      borderColor: 'border-orange-500',
      bgColor: 'bg-orange-50',
      darkBgColor: 'bg-orange-900/30',
      titleColor: 'text-orange-800',
      darkTitleColor: 'text-orange-300',
      textColor: 'text-orange-700',
      darkTextColor: 'text-orange-400',
    })
  }

  const handleCreateSalesTicket = async () => {
    if (!onCreateSalesTicket) return
    setSalesTicketState('loading')
    setSalesTicketError(null)
    try {
      const result = await onCreateSalesTicket()
      if (result?.ticketId) {
        setSalesTicketId(result.ticketId)
      }
      setSalesTicketState('success')
    } catch (err) {
      setSalesTicketError(err instanceof Error ? err.message : 'Failed to create ticket')
      setSalesTicketState('error')
    }
  }

  const alertCount = alerts.length + (hasSalesOpp ? 1 : 0)

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className={`flex items-center gap-2 px-1 ${isDark ? 'text-slate-200' : 'text-foreground'}`}>
        <Bell className="w-4 h-4" />
        <h3 className="text-sm font-medium">Alerts</h3>
        {alertCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {alertCount}
          </span>
        )}
      </div>

      {alertCount === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500 opacity-60" />
          <p className="text-sm font-medium">No alerts</p>
          <p className="text-xs mt-1 opacity-70">No issues requiring attention were detected.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Sales Opportunity Card */}
          {hasSalesOpp && (
            <div className={`rounded-lg border-l-4 p-3 border-emerald-500 ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
              <div className="flex items-start gap-3">
                <div className="p-1.5 rounded-full flex-shrink-0 bg-emerald-500">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
                    Sales Opportunity Detected
                  </h4>
                  <p className={`text-xs mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                    {salesOpportunityData?.summary || 'Potential sales opportunity identified.'}
                  </p>
                  {salesOpportunityData?.type && (
                    <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-800/60 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                      {salesOpportunityData.type}
                    </span>
                  )}
                  {salesOpportunityData?.signals && salesOpportunityData.signals.length > 0 && (
                    <ul className={`text-xs mt-2 space-y-0.5 ${isDark ? 'text-emerald-400' : 'text-emerald-700'} opacity-80`}>
                      {salesOpportunityData.signals.slice(0, 3).map((signal, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="mt-1 w-1 h-1 rounded-full bg-current flex-shrink-0" />
                          {signal}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Action: Create Sales Ticket */}
                  <div className="mt-2">
                    {salesTicketState === 'idle' && onCreateSalesTicket && (
                      <button
                        onClick={handleCreateSalesTicket}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${
                          isDark
                            ? 'bg-emerald-700/50 text-emerald-200 hover:bg-emerald-700/70'
                            : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        }`}
                      >
                        <TrendingUp className="w-3 h-3" />
                        Create Sales Opp Ticket
                      </button>
                    )}
                    {salesTicketState === 'loading' && (
                      <span className={`inline-flex items-center gap-1.5 text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Creating ticket...
                      </span>
                    )}
                    {salesTicketState === 'success' && (
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        <CheckCircle className="w-3 h-3" />
                        Ticket #{salesTicketId} created
                        {salesTicketId && (
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        )}
                      </span>
                    )}
                    {salesTicketState === 'error' && (
                      <div>
                        <span className={`inline-flex items-center gap-1.5 text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                          {salesTicketError || 'Failed to create ticket'}
                        </span>
                        <button
                          onClick={handleCreateSalesTicket}
                          className={`ml-2 text-xs underline ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Standard alert cards */}
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`rounded-lg border-l-4 p-3 ${alert.borderColor} ${isDark ? alert.darkBgColor : alert.bgColor}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-1.5 rounded-full flex-shrink-0 ${
                  alert.id === 'multiuser' ? 'bg-violet-500' :
                  alert.id === 'vip' ? 'bg-amber-500' :
                  alert.id === 'escalation' ? 'bg-red-500' :
                  'bg-orange-500'
                }`}>
                  {alert.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold ${isDark ? alert.darkTitleColor : alert.titleColor}`}>
                    {alert.title}
                  </h4>
                  <p className={`text-xs mt-1 ${isDark ? alert.darkTextColor : alert.textColor}`}>
                    {alert.description}
                  </p>
                  {alert.detail && (
                    <p className={`text-xs mt-1 opacity-80 ${isDark ? alert.darkTextColor : alert.textColor}`}>
                      {alert.detail}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
